import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Header from './components/Header';
import Filters from './components/Filters';
import Row from './components/Row';
import TableHeaderSection from './components/TableHeaderSection';
import TableBox from '../../../components/TableBox';
import { HeadCellType, TableContextProvider } from '../../../interfaces/common';
import { MonthlyStockFilters, MonthlyStockResponse } from '../../../interfaces/monthlyStock.interface';
import { buildHeadCells, getYearOptions, getCurrentMonthCode } from '../../../utils/methods';
import {
  VALIDATION_FIELD_MAPPING,
  HEAD_CELL_SORT_MAPPING,
} from '../../../utils/appConstant';
import useSorting from '../../../hooks/useSorting';
import usePagination from '../../../hooks/usePagination';
import useUrlParams from '../../../hooks/useUrlParams';
import useFetch from '../../../hooks/useFetch';
import MonthlyStockService from '../../../services/monthlyStock.service';
import CommonService from '../../../services/common.service';
import { GENERIC_LOOKUP_KEYS } from '../../../utils/appConstant';
import { SortOrder, Modules, AuthRoutes } from '../../../enums';

const MonthlyStockList: React.FC = () => {
  const { t } = useTranslation();

  const [filters, setFilters] = useState<MonthlyStockFilters>({
    searchText: '',
    month: getCurrentMonthCode(),
    year: String(new Date().getFullYear()),
    stockist: '',
    branch: '',
    user: '',
    status: '',
  });

  const [dropdownSearchData, setDropdownSearchData] = useState<any>({
    month: '',
    year: '',
    stockist: '',
    branch: '',
    user: '',
    status: '',
  });

  const headCells: HeadCellType[] = buildHeadCells(
    [
      VALIDATION_FIELD_MAPPING.month,
      VALIDATION_FIELD_MAPPING.stockist,
      VALIDATION_FIELD_MAPPING.branch,
      VALIDATION_FIELD_MAPPING.template,
      VALIDATION_FIELD_MAPPING.uploadedBy,
      VALIDATION_FIELD_MAPPING.status,
      VALIDATION_FIELD_MAPPING.actions,
    ],
    'monthlyStockList'
  );

  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const { order, orderBy, setOrder, setOrderBy } = useSorting({
    defaultOrder: (searchParams.get('order') as SortOrder) || SortOrder.asc,
    defaultOrderBy: searchParams.get('orderBy') || VALIDATION_FIELD_MAPPING.month,
  });

  const { page, perPage, setPage, setPerPage } = usePagination({
    defaultPage: Number(searchParams.get('page')) || 1,
  });

  useUrlParams({
    type: AuthRoutes.monthlyStockList,
    uri: Modules.monthlyStock + AuthRoutes.monthlyStockList,
    perPage,
    page,
    order,
    orderBy,
  });

  const {
    total,
    data: stockData,
    loading,
  } = useFetch({
    service: MonthlyStockService,
    func: 'list',
    payload: {
      page,
      perPage,
      orderBy: HEAD_CELL_SORT_MAPPING.monthlyStockList[orderBy] || orderBy,
      order,
      searchText: filters.searchText?.trim(),
      month: filters.month,
      year: filters.year,
      stockist: filters.stockist,
      branch: filters.branch,
      user: filters.user,
      status: filters.status,
    },
    dependency: [
      order,
      orderBy,
      page,
      perPage,
      filters.searchText,
      filters.month,
      filters.year,
      filters.stockist,
      filters.branch,
      filters.user,
      filters.status,
    ],
  });

  const handleFiltersChange = useCallback((newFilters: MonthlyStockFilters) => {
    setPage(1);
    setOrder(SortOrder.asc);
    setOrderBy(VALIDATION_FIELD_MAPPING.month);
    setFilters(newFilters);
  }, []);

  const handleDropdownSearchInputChange = useCallback(
    (event: any, name: string) => {
      setDropdownSearchData((prev: any) => ({
        ...prev,
        [name]: event.target.value,
      }));
    },
    []
  );

  // ─── Filter Dropdown Lookups ──────────────────────────────────────────────

  // Month (from genericLookup, sorted by id for correct month order)
  const { data: allMonthOptions } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.month, searchText: '', sortBy: 'id' },
    dependency: [],
  });

  // Year (static — computed once)
  const allYearOptions = getYearOptions();

  // Stockist
  const { data: allStockistOptions } = useFetch({
    service: MonthlyStockService,
    func: 'lookupStockist',
    payload: { searchText: '' },
    dependency: [],
  });
  const { data: stockistOptions } = useFetch({
    service: MonthlyStockService,
    func: 'lookupStockist',
    payload: { searchText: dropdownSearchData.stockist },
    dependency: [dropdownSearchData.stockist],
  });

  // Branch (depends on selected stockist)
  const { data: allBranchOptions } = useFetch({
    service: MonthlyStockService,
    func: 'lookupBranch',
    payload: { searchText: '', stockistId: filters.stockist },
    dependency: [filters.stockist],
  });
  const { data: branchOptions } = useFetch({
    service: MonthlyStockService,
    func: 'lookupBranch',
    payload: { searchText: dropdownSearchData.branch, stockistId: filters.stockist },
    dependency: [dropdownSearchData.branch, filters.stockist],
  });

  // User
  const { data: allUserOptions } = useFetch({
    service: MonthlyStockService,
    func: 'lookupUser',
    payload: { searchText: '' },
    dependency: [],
  });
  const { data: userOptions } = useFetch({
    service: MonthlyStockService,
    func: 'lookupUser',
    payload: { searchText: dropdownSearchData.user },
    dependency: [dropdownSearchData.user],
  });

  // Status (from genericLookup)
  const { data: allStatusOptions } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.stockStatus, searchText: '' },
    dependency: [],
  });
  const { data: statusOptions } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.stockStatus, searchText: dropdownSearchData.status },
    dependency: [dropdownSearchData.status],
  });

  const headerSection = <TableHeaderSection total={total} />;

  const values: TableContextProvider = useMemo(() => {
    return {
      headerSection,
      row: Row,
      data: stockData || [],
      pagination: true,
      headCells,
      order,
      orderBy,
      page,
      perPage,
      total,
      loading,
      noDataFoundMessage: t('monthlyStock.list.pageTexts.table.noDataFoundMessage'),
      setPage,
      setPerPage,
      setOrder,
      setOrderBy,
    };
  }, [
    headCells,
    order,
    orderBy,
    page,
    perPage,
    total,
    loading,
    stockData,
    setPage,
    setPerPage,
    setOrder,
    setOrderBy,
  ]);

  return (
    <>
      <Header />

      <Filters
        dropdownSearchData={dropdownSearchData}
        initialFilters={filters}
        onDropdownSearchChange={handleDropdownSearchInputChange}
        onFilterChange={handleFiltersChange}
        monthOptions={(allMonthOptions || []).map((o: any) => ({ label: o.name, value: String(o.id) }))}
        yearOptions={allYearOptions}
        stockistOptions={(stockistOptions || []).map((o: any) => ({ label: o.name, value: o.id }))}
        allStockistOptions={(allStockistOptions || []).map((o: any) => ({ label: o.name, value: o.id }))}
        branchOptions={(branchOptions || []).map((o: any) => ({ label: o.name, value: o.id }))}
        allBranchOptions={(allBranchOptions || []).map((o: any) => ({ label: o.name, value: o.id }))}
        userOptions={(userOptions || []).map((o: any) => ({ label: o.name, value: o.id }))}
        allUserOptions={(allUserOptions || []).map((o: any) => ({ label: o.name, value: o.id }))}
        statusOptions={(statusOptions || []).map((o: any) => ({ label: o.name, value: o.id }))}
        allStatusOptions={(allStatusOptions || []).map((o: any) => ({ label: o.name, value: o.id }))}
      />

      <TableBox tableValues={values} />
    </>
  );
};

export default MonthlyStockList;
